import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Combobox,
  DatePicker,
  ErrorNotice,
  Field,
  Modal,
  SelectInput,
  SkeletonTable,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  OPPORTUNITY_FIELD,
  readOpportunityEdit,
  updateOpportunity,
  type OpportunityEditContext,
  type OpportunityField,
  type OpportunityFieldChanges,
  type RepWriteApplied,
} from '@/lib/sales-workspace';
import { WriteError } from './write-error';

/** The stage that needs a reason. */
export const STAGE_CLOSED_LOST = 'Closed Lost';

/** The groups of fields, one small modal each. */
export const EDIT_GROUP = {
  stage: 'stage',
  details: 'details',
  pricing: 'pricing',
} as const;
export type EditGroup = (typeof EDIT_GROUP)[keyof typeof EDIT_GROUP];

const GROUP_TITLE: Readonly<Record<EditGroup, string>> = {
  stage: 'Change stage',
  details: 'Edit deal details',
  pricing: 'Edit pricing',
};

const FIELD_LABEL: Readonly<Record<OpportunityField, string>> = {
  name: 'Opportunity name',
  stageName: 'Stage',
  reasonLost: 'Reason lost',
  programId: 'Program',
  eventDate: 'Event date',
  participants: 'Participants',
  priceList: 'Price list',
  perPersonPrice: 'Cost per person',
  listPrice: 'List price',
  totalProgramCost: 'Total program cost',
};

const GROUP_FIELDS: Readonly<Record<EditGroup, readonly OpportunityField[]>> = {
  stage: [OPPORTUNITY_FIELD.stageName, OPPORTUNITY_FIELD.reasonLost],
  details: [OPPORTUNITY_FIELD.name, OPPORTUNITY_FIELD.programId, OPPORTUNITY_FIELD.eventDate, OPPORTUNITY_FIELD.participants],
  pricing: [OPPORTUNITY_FIELD.priceList, OPPORTUNITY_FIELD.perPersonPrice, OPPORTUNITY_FIELD.listPrice, OPPORTUNITY_FIELD.totalProgramCost],
};

const NUMBER_FIELDS: ReadonlySet<OpportunityField> = new Set([
  OPPORTUNITY_FIELD.participants,
  OPPORTUNITY_FIELD.perPersonPrice,
  OPPORTUNITY_FIELD.listPrice,
  OPPORTUNITY_FIELD.totalProgramCost,
]);

const NOT_EDITABLE_HINT = 'Your Salesforce login cannot edit this field.';

/** Form state: every value as the text the control holds. */
type Draft = Partial<Record<OpportunityField, string>>;

function toDraft(context: OpportunityEditContext, fields: readonly OpportunityField[]): Draft {
  return Object.fromEntries(fields.map((field) => [field, context.values[field] === null ? '' : String(context.values[field])]));
}

/** Text → the API's value: '' clears, numbers parse; undefined when a number does not. */
function parse(field: OpportunityField, text: string): string | number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!NUMBER_FIELDS.has(field)) return trimmed;
  const value = Number(trimmed.replace(/[$,]/g, ''));
  if (!Number.isFinite(value) || value < 0) return undefined;
  return field === OPPORTUNITY_FIELD.participants && !Number.isInteger(value) ? undefined : value;
}

/**
 * Only the fields the rep changed, parsed; null when one does not parse.
 * Pure: exported for tests.
 */
export function changedFields(context: OpportunityEditContext, fields: readonly OpportunityField[], draft: Draft): OpportunityFieldChanges | null {
  const changes: OpportunityFieldChanges = {};
  for (const field of fields) {
    const text = draft[field];
    if (text === undefined) continue;
    const value = parse(field, text);
    if (value === undefined) return null;
    if (value !== context.values[field]) changes[field] = value;
  }
  // A reason travels with Closed Lost, both ways: the API takes one only with
  // that stage, and that stage only with one.
  const stage = changes.stageName ?? context.values.stageName;
  if (stage !== STAGE_CLOSED_LOST) {
    delete changes.reasonLost;
  } else if (changes.stageName !== undefined || changes.reasonLost !== undefined) {
    changes.stageName = STAGE_CLOSED_LOST;
    if (changes.reasonLost === undefined) changes.reasonLost = context.values.reasonLost;
  }
  return changes;
}

export interface EditOpportunityDialogProps {
  readonly opportunityId: string;
  /** Which group to edit; null closes the dialog. */
  readonly group: EditGroup | null;
  readonly onClose: () => void;
  readonly onDone: (result: RepWriteApplied) => void;
}

/**
 * Edits one group of a deal's fields as the rep. It opens with a live read
 * (values, which fields the rep's login may edit, the picklists), and sends
 * back the LastModifiedDate it read, so a change made in Salesforce meanwhile
 * is refused rather than overwritten.
 */
export function EditOpportunityDialog({ opportunityId, group, onClose, onDone }: EditOpportunityDialogProps): React.ReactElement | null {
  const context = useQuery({
    queryKey: ['sales-opportunity-edit', opportunityId],
    queryFn: ({ signal }) => readOpportunityEdit(opportunityId, signal),
    enabled: group !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  if (group === null) return null;
  const title = GROUP_TITLE[group];

  if (context.isPending || context.isError) {
    return (
      <Modal
        open
        title={title}
        onClose={onClose}
        actions={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        {context.isError ? (
          <ErrorNotice inline title="Could not read the deal from Salesforce." message={errorMessage(context.error)} />
        ) : (
          <SkeletonTable rows={3} columns={['w-[40%]', 'w-[60%]']} label="Reading the deal from Salesforce…" />
        )}
      </Modal>
    );
  }
  return (
    <EditForm
      // A reload brings a new LastModifiedDate: start the form over from it.
      key={`${group}-${context.data.lastModified}`}
      opportunityId={opportunityId}
      group={group}
      context={context.data}
      onClose={onClose}
      onDone={onDone}
      onReload={() => void context.refetch()}
    />
  );
}

function EditForm({
  opportunityId,
  group,
  context,
  onClose,
  onDone,
  onReload,
}: {
  readonly opportunityId: string;
  readonly group: EditGroup;
  readonly context: OpportunityEditContext;
  readonly onClose: () => void;
  readonly onDone: (result: RepWriteApplied) => void;
  readonly onReload: () => void;
}): React.ReactElement {
  const fields = GROUP_FIELDS[group];
  const [draft, setDraft] = useState<Draft>(() => toDraft(context, fields));
  const set = (field: OpportunityField) => (value: string) => setDraft((previous) => ({ ...previous, [field]: value }));

  const changes = changedFields(context, fields, draft);
  const needsReason = changes?.stageName === STAGE_CLOSED_LOST && !changes.reasonLost;
  const blocked = context.signed || context.locked;
  const ready = !blocked && changes !== null && Object.keys(changes).length > 0 && !needsReason;

  const save = useMutation({
    mutationFn: () => updateOpportunity(opportunityId, { expectedLastModified: context.lastModified, fields: changes ?? {} }),
    onSuccess: onDone,
  });

  const control = (field: OpportunityField): React.ReactElement => {
    const id = `opportunity-${field}`;
    const { editable, options } = context.fields[field];
    const value = draft[field] ?? '';
    const hint = editable ? undefined : NOT_EDITABLE_HINT;
    const disabled = !editable || blocked || save.isPending;
    if (field === OPPORTUNITY_FIELD.programId) {
      return (
        <Field key={field} label={FIELD_LABEL[field]} htmlFor={id} {...(hint ? { hint } : {})}>
          <div className="mt-1.5">
            <Combobox
              id={id}
              value={value}
              onChange={set(field)}
              noneLabel="No program"
              placeholder="Search programs"
              disabled={disabled}
              options={context.programs.map((program) => ({ value: program.id, label: program.name }))}
            />
          </div>
        </Field>
      );
    }
    if (field === OPPORTUNITY_FIELD.eventDate) {
      return (
        <Field key={field} label={FIELD_LABEL[field]} htmlFor={id} {...(hint ? { hint } : {})}>
          <DatePicker id={id} value={value} onChange={set(field)} disabled={disabled} />
        </Field>
      );
    }
    if (field === OPPORTUNITY_FIELD.stageName || field === OPPORTUNITY_FIELD.reasonLost || field === OPPORTUNITY_FIELD.priceList) {
      return (
        <Field key={field} label={FIELD_LABEL[field]} htmlFor={id} required={field !== OPPORTUNITY_FIELD.priceList} {...(hint ? { hint } : {})}>
          <SelectInput id={id} value={value} disabled={disabled} onChange={(event) => set(field)(event.target.value)}>
            {field === OPPORTUNITY_FIELD.stageName ? null : <option value="">{field === OPPORTUNITY_FIELD.reasonLost ? 'Pick a reason' : 'None'}</option>}
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </SelectInput>
        </Field>
      );
    }
    return (
      <Field key={field} label={FIELD_LABEL[field]} htmlFor={id} required={field === OPPORTUNITY_FIELD.name} {...(hint ? { hint } : {})}>
        <TextInput
          id={id}
          value={value}
          disabled={disabled}
          inputMode={NUMBER_FIELDS.has(field) ? (field === OPPORTUNITY_FIELD.participants ? 'numeric' : 'decimal') : undefined}
          maxLength={field === OPPORTUNITY_FIELD.name ? 120 : undefined}
          onChange={(event) => set(field)(event.target.value)}
        />
      </Field>
    );
  };

  const visible = fields.filter((field) => field !== OPPORTUNITY_FIELD.reasonLost || draft.stageName === STAGE_CLOSED_LOST);

  return (
    <Modal
      open
      title={GROUP_TITLE[group]}
      onClose={() => {
        if (!save.isPending) onClose();
      }}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {context.signed ? (
          <ErrorNotice inline tone="warning" message="This deal is signed. Change it through its contract, or in Salesforce." />
        ) : context.locked ? (
          <ErrorNotice inline tone="warning" message="This opportunity is locked in Salesforce." />
        ) : null}
        {visible.map(control)}
        {changes === null ? <p className="text-portal-micro text-rose-600">Enter amounts as numbers, like 95 or 3800.</p> : null}
        {save.isError ? <WriteError error={save.error} onReload={onReload} /> : null}
      </div>
    </Modal>
  );
}
